import { useCallback } from "react";

import { toast } from "@/lib/ui/app-toast";

import { recoveryActionKey } from "../../lib/errors/request-failure";
import { usePresentUserFacingError } from "./use-present-user-facing-error";
import { useErrorRecoveryHandlers } from "./user-facing-error-state";
import { useAppI18n } from "../../i18n/context";

type ToastUserFacingErrorOptions = {
  onGoBack?: () => void;
  onGoHome?: () => void;
  onRetry?: () => void;
  onSignIn?: () => void;
};

/** 把 present 结果接到 toast：不同错误不同提示，并带对应按钮。 */
export const useToastUserFacingError = () => {
  const { t } = useAppI18n();
  const present = usePresentUserFacingError();
  const { onGoBack, onGoHome, onRetry, onSignIn } = useErrorRecoveryHandlers();

  return useCallback(
    (err: unknown, options?: ToastUserFacingErrorOptions) => {
      const presented = present(err, "toast");
      const handlers = {
        onGoBack: options?.onGoBack ?? onGoBack,
        onGoHome: options?.onGoHome ?? onGoHome,
        onRetry: options?.onRetry ?? onRetry,
        onSignIn: options?.onSignIn ?? onSignIn,
      };

      if (presented.kind === "specific") {
        toast.danger(presented.description);
        return;
      }

      const primary = presented.plan.primary;
      const onPress =
        primary === "refresh" || primary === "retry"
          ? handlers.onRetry
          : primary === "signIn"
            ? handlers.onSignIn
            : primary === "goBack"
              ? handlers.onGoBack
              : primary === "goHome"
                ? handlers.onGoHome
                : undefined;

      const showToast =
        presented.tone === "warning" ? toast.warning : toast.danger;

      showToast(t(presented.titleKey), {
        description: presented.description,
        timeout: primary ? 10_000 : 5_000,
        actionProps:
          primary && onPress
            ? {
                children: t(recoveryActionKey(primary)),
                onPress,
              }
            : undefined,
      });
    },
    [onGoBack, onGoHome, onRetry, onSignIn, present, t],
  );
};
